import React from 'react';

/**
 * WorkforceBadge — colour-coded pill identifying a worker's source.
 *
 *   INTERNAL   → emerald   (in-house employees)
 *   FREELANCER → amber
 *   VENDOR     → violet   (vendor_producer / vendor_artist)
 *
 * Reused across:
 *   - AdminAllotmentPage (CBA tab per-row picker, bulk-assign modal)
 *   - SubmissionPipelinePanel (per-row Assign modal on every other tab)
 *   - Any future allotment surface
 *
 * Spec restored Feb 14, 2026 after founder pointed out the badges had
 * gone missing in a UI cleanup. One source of truth for the badge
 * keeps colors consistent across screens.
 */
export default function WorkforceBadge({ user, size = 'sm' }) {
  if (!user) return null;
  const wt = (user.workforce_type || '').toLowerCase();
  const role = (user.role || '').toLowerCase();
  const isVendor = wt === 'vendor_producer' || wt === 'vendor_artist'
    || role.startsWith('vendor');
  const isFree = wt === 'freelancer' || role === 'freelancer';

  let label = 'INTERNAL';
  let cls = 'bg-emerald-500/15 text-emerald-300 border-emerald-500/25';
  if (isVendor) {
    label = role === 'vendor_producer' ? 'VENDOR PROD' : 'VENDOR';
    cls = 'bg-violet-500/15 text-violet-300 border-violet-500/25';
  } else if (isFree) {
    label = 'FREELANCER';
    cls = 'bg-amber-500/15 text-amber-300 border-amber-500/25';
  }
  const sizeCls = size === 'md'
    ? 'text-[10px] px-2 py-0.5'
    : 'text-[9px] px-1.5 py-0.5';
  return (
    <span
      className={`font-bold uppercase tracking-wider rounded-sm border ${sizeCls} ${cls}`}
      data-testid={`workforce-badge-${user.id}`}
    >
      {label}
    </span>
  );
}

/** Classification helper — used to group lists of workers. */
export function classifyWorkforce(user) {
  const wt = (user.workforce_type || '').toLowerCase();
  const role = (user.role || '').toLowerCase();
  if (wt === 'vendor_producer' || wt === 'vendor_artist'
      || role.startsWith('vendor')) return 'vendor';
  if (wt === 'freelancer' || role === 'freelancer') return 'freelancer';
  return 'internal';
}
